import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { isPasswordValid, validatePassword } from '../utils/passwordValidation';
import { ErrorMessage, PasswordInput, PasswordValidationDisplay, TextInput } from './shared';

interface RegisterPageProps {
  onSwitchToLogin: () => void;
}

const RegisterPage: React.FC<RegisterPageProps> = ({ onSwitchToLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState(validatePassword(''));
  const { register } = useAuth();

  const handlePasswordChange = (newPassword: string) => {
    setPassword(newPassword);
    setPasswordValidation(validatePassword(newPassword));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username.trim()) {
      setError('Username is required');
      return;
    }

    if (username.trim().length < 2) {
      setError('Username must be at least 2 characters long');
      return;
    }

    if (!password) {
      setError('Password is required');
      return;
    }

    if (!isPasswordValid(passwordValidation)) {
      setError('Password does not meet the required criteria');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      const result = await register(username.trim(), password);
      if (!result.success) {
        setError(result.message || 'Registration failed');
      }
    } catch {
      setError('Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-[#0C233E]">Create Account</h2>
        <p className="text-slate-600 mt-2">Register for a new account</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <TextInput
          id="username"
          label="Username"
          value={username}
          onChange={setUsername}
          placeholder="Enter your username"
          autoComplete="username"
          autoFocus
          disabled={isSubmitting}
          required
          minLength={2}
        />

        <PasswordInput
          id="password"
          label="Password"
          value={password}
          onChange={handlePasswordChange}
          placeholder="Enter your password"
          autoComplete="new-password"
          disabled={isSubmitting}
          required
        />

        <PasswordValidationDisplay password={password} validation={passwordValidation} />

        <PasswordInput
          id="confirmPassword"
          label="Confirm Password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          placeholder="Confirm your password"
          autoComplete="new-password"
          disabled={isSubmitting}
          required
        />

        <ErrorMessage message={error} show={!!error} />

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#0C233E] hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0C233E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Creating account...' : 'Create Account'}
        </button>

        <div className="text-center">
          <button type="button" onClick={onSwitchToLogin} className="text-sm text-[#0C233E] hover:underline" disabled={isSubmitting}>
            Already have an account? Sign in
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegisterPage;
